import { useEffect, useState } from "react";
import { Form, Input, Select, Button, Checkbox, Card, Divider, Col, Row, Radio } from "antd";
import { useTranslation } from "react-i18next";
import {
  CodebookDto,
  RequestType,
} from "api/models";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import "./create-request.css";
import { PublicApi } from "api/api";
import { useNavigate, useParams } from "react-router-dom";
import openNotificationWithIcon from "utility/notification";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { transformRequestData, transformRequestFormData1, transformRequestFormData2 } from "./transformRequestFormData";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const publicApi = new PublicApi();

const { Option } = Select;

const CreateRequestsForm = () => {
  /**
   * Transalte
   */
  const { t } = useTranslation();

  const navigate = useNavigate();

  /**
   * Forms
   */
  const [form] = Form.useForm();

  const { type } = useParams();
  const parsedType = parseInt(type ?? "0") as RequestType;


  /**
   * State
   */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codebooks, setCodebooks] = useState<{ [key: string]: CodebookDto[] }>({});
  const { executeRecaptcha } = useGoogleReCaptcha();

  const onFinish = async (data: any) => {
    setIsSubmitting(true);
    await searchForContactPersonEmail();

    if (isFormInvalid()) {
      let errorAlias = "requests:create.company-not-found";
      let errorDesc = "You are already part of the system, please sign in with your credentials and make requests from there!";

      openNotificationWithIcon(
        "error",
        t(errorAlias, errorDesc)
      );
      setIsSubmitting(false);
      return;
    }

    const languageId =
      localStorage.getItem("i18nextLanguageId") ||
      "649251f1-7f38-11ee-85aa-0242ac110004";
    try {
      setIsSubmitting(true);
      data.type = parsedType;
      data.hasVatIdAndAcceptsTerms = true;

      data.languageId = languageId;
      let withQuestionsData;

      if (parsedType === 1) {
        withQuestionsData = transformRequestFormData1(data)
      } else {
        withQuestionsData = transformRequestFormData2(data)
      }

      // Generate reCAPTCHA token
      let recaptchaToken: string | null = null;

      try {
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("register");

          if (recaptchaToken && recaptchaToken.trim() !== "") {
            const tokenPreview = recaptchaToken.length > 20
              ? `${recaptchaToken.substring(0, 20)}...`
              : recaptchaToken;
          } else {
            console.warn("reCAPTCHA token is empty after generation. This may cause 'browser-error' on the backend.");
          }
        } else {
          console.warn("executeRecaptcha is not available. reCAPTCHA may not be initialized.");
        }
      } catch (error) {
        console.error("Error generating reCAPTCHA token:", error);
        openNotificationWithIcon(
          "error",
          t("requests:create.recaptcha-error", "reCAPTCHA verification failed. Please try again.")
        );
        setIsSubmitting(false);
        return;
      }

      // Add reCaptcha token to the request
      const response:any = await publicApi.apiPublicRequestsSubmitPost({
        ...withQuestionsData,
        recaptchaToken: recaptchaToken
      } as any);
      if (response.data.succeeded) {
        if (type === "2" && data?.certificationNumbers?.length != data.totalNumberOfServiceTechnians) {
          let errorAlias = "requests:create.certification-numbers-count-same";
          let errorDesc = "The total number of service technicians should match the number of selected Certified service technicians. Please review and update the counts.";

          openNotificationWithIcon(
            "error",
            t(errorAlias, errorDesc)
          );
          setIsSubmitting(false);
          return;
        }

        delete data.areaOfExpertise;
        delete data.companyType;
        // delete data.type;
        delete data.languageId;
        delete data.stateEntityId;
        delete data.businessActivityId;
        delete data.totalNumberOfServiceTechnians;
        delete data.certificationNumbers;
        const modifyData = transformRequestData(data, data.type, codebooks);
        modifyData.companyPhoneNumber = "+ " + modifyData.companyPhoneNumber;
        delete modifyData.type;
        navigate("/confirmation", {
          state: {
            requestId: response.data.requestId,
            ...modifyData,
          },
        });

        form.resetFields();
        openNotificationWithIcon(
          "success",
          t("requests:create-success", "Request created successfully")
        );
      }else{
        openNotificationWithIcon(
          "error",
          response.data.message
        );
      }


    } catch (error) {
      console.log(error);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormInvalid = () =>
    form.getFieldsError().some((item) => item.errors?.length > 0);


  const searchForContactPersonEmail = async () => {
    const contactPersonEmail = form.getFieldValue("contactPersonEmail");
    const { data } = await publicApi.apiPublicUserExistsEmailGet({
      email: contactPersonEmail,
    });
    if (data) {
      form.setFields([
        {
          name: "contactPersonEmail",
          errors: [
            t(
              "request:conctactPersonEmail-exist",
              "You are already part of the system, please sign in with your credentials and make requests from there!"
            ),
          ],
        },
      ]);
    }
  };



  /**
   * Use Effect
   */
  useEffect(() => {
    const fetchCodebooks = async () => {
      const { data } = await publicApi.apiPublicCodebooksGet();
      setCodebooks(data);
    };

    fetchCodebooks();
  }, []);

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedType]);

  // Check reCAPTCHA initialization
  useEffect(() => {
    // Check if reCAPTCHA script is loaded
    const checkRecaptchaLoaded = () => {
      if (typeof window !== 'undefined') {
        const grecaptcha = (window as any).grecaptcha;
        if (grecaptcha) {
          console.log("reCAPTCHA script is loaded");
        } else {
          console.warn("reCAPTCHA script (grecaptcha) not found. Check if script loaded properly.");
        }
      }
    };

    // Check immediately
    checkRecaptchaLoaded();

    // Also check after a delay (script might still be loading)
    const timeout = setTimeout(checkRecaptchaLoaded, 2000);

    // executeRecaptcha is always available from the hook, but may throw if provider isn't ready

    return () => clearTimeout(timeout);
  }, [executeRecaptcha]);

  return (
    <>
      {/* <div className="request-overlay"> */}
      {parsedType === 1 && (
        <div className="request-overlay overlay-1">
          <p className="title-text">
            {t(
              "request:register-as-purchaser:title",
              "Register as Purchaser."
            )}
          </p>
        </div>
      )}
      {parsedType === 2 && (
        <div className="request-overlay overlay-1">
          <p className="title-text">
            {t(
              "request:register-as-carrier:title",
              "Register as Carrier."
            )}
          </p>
        </div>
      )}

      {/* </div> */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px', marginBottom: '50px' }}>
        <Card className="request-container">
          <Form
            form={form}
            name="request"
            requiredMark
            onFinish={onFinish}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            scrollToFirstError
          >
            <div className="request-div" style={{ marginTop: 0 }}>

              <h1 className="request-title">
                {t("register:contact-information", "Contact Information")}
              </h1>
              <Divider style={{ backgroundColor: '#0000002b' }} />
              <Row >
                <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                  <Form.Item
                    name="contactPersonFirstName"
                    label={t(
                      "register:first-name",
                      "First Name"
                    )}
                    rules={[
                      {
                        required: true,
                        message: t("validations.required-field", {
                          field: t("register:first-name", "First Name"),
                          defaultValue: "{{field}} is required",
                        }),
                      },
                      {
                        pattern: /^(?!\s)(?!.*\s$).+/,
                        message: t("validations.no-whitespace", {
                          field: t("register:first-name", "First Name"),
                          defaultValue: "{{field}} cannot be empty or whitespace only."
                        }),
                      },
                    ]}
                    required
                  >
                    <Input className="w-full" />
                  </Form.Item>
                </Col>
                <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                  <Form.Item
                    name="contactPersonLastName"
                    label={t(
                      "register:last-name",
                      "Last Name"
                    )}
                    rules={[
                      {
                        required: true,
                        message: t("validations.required-field", {
                          field: t("register:last-name", "Last Name"),
                          defaultValue: "{{field}} is required",
                        }),
                      },
                      {
                        pattern: /^(?!\s)(?!.*\s$).+/,
                        message: t("validations.no-whitespace", {
                          field: t("register:last-name", "Last Name"),
                          defaultValue: "{{field}} cannot be empty or whitespace only."
                        }),
                      },
                    ]}
                    required
                  >
                    <Input className="w-full" />
                  </Form.Item>
                </Col>

                <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                  <Form.Item
                    name="contactPersonEmail"
                    label={t("register:email-address", "Email address")}
                    rules={[
                      {
                        required: true,
                        message: t("validations.required-field", {
                          field: t("register:email-address", "Email address"),
                          defaultValue: "{{field}} is required",
                        }),
                      },
                      {
                        // pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        pattern: /^(?!.*\.\.)[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t(
                          "users.create:email-validation",
                          "Invalid email format"
                        ),
                      },
                    ]}
                    required
                  >
                    <Input className="w-full" />
                  </Form.Item>
                </Col>
              </Row>
              <Row style={{ marginTop: '30px' }}>

                <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                  <Form.Item
                    name="companyPhoneNumber"
                    label={t("global.phone-number", "Phone number")}
                    rules={[
                      {
                        required: true,
                        message: t("validations.required-field", {
                          field: t("global.phone-number", "Phone number"),
                          defaultValue: "{{field}} is required",
                        }),
                      },
                    ]}
                  >
                    <PhoneInput
                      country={"us"}
                      enableSearch={true}
                      containerStyle={{ width: '100%' }}
                      inputStyle={{ width: '100%' }}
                      // dropdownStyle={{ maxHeight: "300px" }} // scroll if too many countries
                      inputClass="ant-input"
                    />
                  </Form.Item>

                  <Form.Item name="receiveNews" valuePropName="checked" className="requirement-text">
                    <Checkbox style={{ marginTop: "30px" }}>
                      {t("register:i-would-like-to-receive-news", "I would like to receive news")}
                    </Checkbox>
                  </Form.Item>

                  {/* VAT ID acceptance */}
                  <Form.Item
                    className="requirement-text"
                    name="hasVatIdAndAcceptsTerms"
                  >
                    <Checkbox checked={true}>
                      {t(
                        "register:i-have-vat-id-and-accept-terms-conditions",
                        "I have VAT ID and accept Terms & Conditions"
                      )}
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div className="request-container-div flex justify-center flex-col md:flex-row ">
              <div className="request-div" >
                <h1 className="request-title">
                  {t("request:company-information", "Company Information")}
                </h1>
                <Divider style={{ backgroundColor: '#0000002b' }} />
                <Row>
                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item
                      name="companyName"
                      label={t("requests:create.company-name", "Company Name")}
                      required
                      rules={[
                        {
                          required: true,
                          message: t("validations.required-field", {
                            field: t("requests:create.company-name", "Company Name"),
                            defaultValue: "{{field}} is required",
                          }),
                        },
                        {
                          pattern: /^(?!\s)(?!.*\s$).+/,
                          message: t("validations.no-whitespace", {
                            field: t("requests:create.company-name", "Company Name"),
                            defaultValue: "{{field}} cannot be empty or whitespace only."
                          }),
                        },
                      ]}
                    >
                      <Input className="w-full" />
                    </Form.Item>
                  </Col>

                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item
                      name="taxNumber"
                      label={t("requests:create.tax-number", "Tax Number")}
                      rules={[
                        {
                          len: 12,
                          required: true,
                          message: t(
                            "request:create-tax-number-validation",
                            "Tax Number should be exact 12 characters"
                          ),
                        },
                        {
                          pattern: /^(?!\s)(?!.*\s$).+/,
                          message: t("validations.no-whitespace", {
                            field: t("requests:create.tax-number", "Tax Number"),
                            defaultValue: "{{field}} cannot be empty or whitespace only."
                          }),
                        },
                      ]}
                    >
                      <Input className="w-full" maxLength={12} />
                    </Form.Item>
                  </Col>
                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item
                      name="companyEmailAddress"
                      label={t(
                        "request:create.primary-email",
                        "Company Primary E-mail"
                      )}
                      rules={[
                        {
                          required: true,
                          message: t("validations.required-field", {
                            field: t("request:create.primary-email", "Company Primary E-mail"),
                            defaultValue: "{{field}} is required",
                          }),
                        },
                        {
                          // pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          pattern: /^(?!.*\.\.)[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t(
                            "users.create:email-validation",
                            "Invalid email format"
                          ),
                        },
                      ]}

                    >
                      <Input className="w-full" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row style={{ marginTop: '30px' }}>
                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item
                      name="countryId"
                      required
                      label={t("register:country", "Country")}
                      // requiredMark
                      rules={[
                        {
                          required: true,
                          message: t("validations.required-select-field", {
                            field: t("register:country", "Country"),
                            defaultValue: "Please select {{field}}",
                          }),
                        },
                      ]}
                    >
                      <Select
                        className="sDash_fullwidth-select"
                        aria-required
                        size="large"
                        style={{ color: "rgb(90, 95, 125)" }}
                      >
                        {codebooks["Country"] &&
                          codebooks["Country"]?.map((item) => (
                            <Option key={item.id} value={item.id}>
                              {item.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item
                      name="address"
                      label={t("global.address", "Address")}
                      required
                      rules={[
                        {
                          required: true,
                          message: t("validations.required-field", {
                            field: t("global.address", "Address"),
                            defaultValue: "{{field}} is required",
                          }),
                        },
                        {
                          pattern: /^(?!\s)(?!.*\s$).+/,
                          message: t("validations.no-whitespace", {
                            field: t("global.address", "Address"),
                            defaultValue: "{{field}} cannot be empty or whitespace only."
                          }),
                        },
                      ]}
                    >
                      <Input className="w-full" />
                    </Form.Item>
                  </Col>
                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item name="place" label={t("register:city", "City")} required
                      rules={[
                        {
                          required: true,
                          message: t("validations.required-field", {
                            field: t("register:city", "City"),
                            defaultValue: "{{field}} is required",
                          }),
                        },
                        {
                          pattern: /^(?!\s)(?!.*\s$).+/,
                          message: t("validations.no-whitespace", {
                            field: t("register:city", "City"),
                            defaultValue: "{{field}} cannot be empty or whitespace only."
                          }),
                        },
                      ]}
                    >
                      <Input className="w-full" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row style={{ marginTop: '30px' }}>
                  <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                    <Form.Item name="postCode" label={t("register:postal-code", "Postal Code")} required
                      rules={[
                        {
                          required: true,
                          message: t("validations.required-field", {
                            field: t("register:postal-code", "Postal Code"),
                            defaultValue: "{{field}} is required",
                          }),
                        },
                        {
                          pattern: /^(?!\s)(?!.*\s$).+/,
                          message: t("validations.no-whitespace", {
                            field: t("register:postal-code", "Postal Code"),
                            defaultValue: "{{field}} cannot be empty or whitespace only."
                          }),
                        },
                      ]}
                    >
                      <Input className="w-full" />
                    </Form.Item>
                  </Col>
                  {type?.toString() === "2" && (
                    <>
                      <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                        <Form.Item
                          name="smtNumber"
                          label={t("request:create.smt-number", "SMT Number")}
                          rules={[
                            {
                              required: true,
                              message: t("validations.required-field", {
                                field: t("request:create.smt-number", "SMT Number"),
                                defaultValue: "{{field}} is required",
                              }),
                            },
                            {
                              pattern: /^(?!\s)(?!.*\s$).+/,
                              message: t("validations.no-whitespace", {
                                field: t("request:create.smt-number", "SMT Number"),
                                defaultValue:
                                  "{{field}} cannot be empty or whitespace only.",
                              }),
                            },
                          ]}
                        >
                          <Input className="w-full" />
                        </Form.Item>
                      </Col>

                      <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                        <Form.Item
                          name="cipCidNumber"
                          label={t("request:create.cip-cid-number", "CIP/CID Number")}
                          rules={[
                            {
                              required: true,
                              message: t("validations.required-field", {
                                field: t("request:create.cip-cid-number", "CIP/CID Number"),
                                defaultValue: "{{field}} is required",
                              }),
                            },
                            {
                              pattern: /^(?!\s)(?!.*\s$).+/,
                              message: t("validations.no-whitespace", {
                                field: t("request:create.cip-cid-number", "CIP/CID Number"),
                                defaultValue:
                                  "{{field}} cannot be empty or whitespace only.",
                              }),
                            },
                          ]}
                        >
                          <Input className="w-full" />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
                {type?.toString() === "2" && (
                  <>
                    <Row style={{ marginTop: "30px" }}>
                      <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                        <Form.Item
                          name="aconNumber"
                          label={t("request:create.acon-number", "ACON No.")}
                          rules={[
                            {
                              required: true,
                              message: t("validations.required-field", {
                                field: t("request:create.acon-number", "ACON No."),
                                defaultValue: "{{field}} is required",
                              }),
                            },
                            {
                              pattern: /^(?!\s)(?!.*\s$).+/,
                              message: t("validations.no-whitespace", {
                                field: t("request:create.acon-number", "ACON No."),
                                defaultValue:
                                  "{{field}} cannot be empty or whitespace only.",
                              }),
                            },
                          ]}
                        >
                          <Input className="w-full" />
                        </Form.Item>
                      </Col>
                      <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                        <Form.Item
                          name="vatNumber"
                          label={t("request:create.vat-number", "VAT Number")}
                          rules={[
                            {
                              required: true,
                              message: t("validations.required-field", {
                                field: t("request:create.vat-number", "VAT Number"),
                                defaultValue: "{{field}} is required",
                              }),
                            },
                            {
                              pattern: /^(?!\s)(?!.*\s$).+/,
                              message: t("validations.no-whitespace", {
                                field: t("request:create.vat-number", "VAT Number"),
                                defaultValue:
                                  "{{field}} cannot be empty or whitespace only.",
                              }),
                            },
                          ]}
                        >
                          <Input className="w-full" />
                        </Form.Item>
                      </Col>

                      <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                        <Form.Item
                          name="roadLog"
                          label={t("request:create.road-log", "Road LOG")}
                          rules={[
                            {
                              required: true,
                              message: t("validations.required-field", {
                                field: t("request:create.road-log", "Road LOG"),
                                defaultValue: "{{field}} is required",
                              }),
                            },
                            {
                              pattern: /^(?!\s)(?!.*\s$).+/,
                              message: t("validations.no-whitespace", {
                                field: t("request:create.road-log", "Road LOG"),
                                defaultValue:
                                  "{{field}} cannot be empty or whitespace only.",
                              }),
                            },
                          ]}
                        >
                          <Input className="w-full" />
                        </Form.Item>
                      </Col>

                    </Row>
                  </>
                )}
              </div>
            </div>
            {/*  */}

            <div className="request-div">
              <h1 className="request-title">
                {t("register:Questionnaire", "Questionnaire")}
              </h1>
              <Divider style={{ backgroundColor: "#0000002b" }} />
              {type?.toString() === "1" && (
                <>
                  <Row gutter={[24, 24]}>

                    {/* Q1. How often do you ship? */}
                    <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                      <Form.Item
                        name="question_1_value"
                        label={t("register:how-often-do-you-ship", "1. How often do you ship?")}
                        rules={[{ required: true, message: t("validations.required", "Please select one option") }]}
                      >
                        <Radio.Group>
                          <div className="requirement-text mt-2">
                            <Radio value="1">
                              {t("register:i-have-only-one-shipment", "I have only one shipment")}
                            </Radio>
                          </div>
                          <div className="requirement-text mt-2">
                            <Radio value="2">
                              {t("register:i-ship-frequently", "I ship frequently")}
                            </Radio>
                          </div>
                        </Radio.Group>
                      </Form.Item>
                    </Col>

                    {/* Q2. What goods are you planning to ship? */}
                    <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                      <Form.Item
                        name="question_2_codebookId"
                        label={t("register:What-goods-are-you-planning-to-ship", "2. What goods are you planning to ship?")}
                        rules={[{ required: true, message: t("validations.required.goods", "Please select at least one good") }]}
                      >
                        <Select
                          mode="multiple"
                          maxTagCount="responsive"
                          placeholder={t("register:dropdown-list-of-predefined-goods", "Dropdown list of predefined goods")}
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }

                        >
                          {codebooks["GoodsType"] &&
                            codebooks["GoodsType"]?.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Q3. What size are your shipments? */}
                    <Col lg={8} md={12} sm={24} xs={24} className="input-field">
                      <Form.Item
                        name="question_3_trailerQTY"
                        label={t("register:What-size-are-your-shipments", "3. What size are your shipments?")}
                        rules={[{ required: true, message: t("validations.required.shipment-size", "Please select at least one size") }]}
                      >
                        <Checkbox.Group>
                          <div className="requirement-text mt-2">
                            <Checkbox value="1">{t("register:LTL", "LTL")}</Checkbox>
                          </div>
                          <div className="requirement-text mt-2">
                            <Checkbox value="2">{t("register:groupage", "Groupage")}</Checkbox>
                          </div>
                          <div className="requirement-text mt-2">
                            <Checkbox value="3">{t("register:FTL", "FTL")}</Checkbox>
                          </div>
                        </Checkbox.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              {type?.toString() === "2" && (
                <>

                  {/* 1. Total number of vehicles */}
                  < Row style={{ marginBottom: 12 }}>
                    <Col lg={12} md={24}>
                      <Form.Item
                        name="question_1_values"
                        label={t("register:questionnaire.total-number-of-vehicles-in-your-fleet", "1. What is the total number of vehicles in your fleet?")}
                        rules={[{ required: true, message: t("validations.required.total-number-of-vehicles", "Total number of vehicles is required") }]}
                      >
                        <Input type="number" min={0} className="w-full" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* 2. Trailers by type */}
                  <Row>
                    <Col span={24}>
                      <Form.List name="trailers" initialValue={[{}]}>
                        {(fields, { add, remove }) => (
                          <>
                            <label>
                              {t(
                                "register:questionnaire.trailer-type",
                                "2. How many trailers do you operate by trailer type?"
                              )}
                            </label>
                            {fields.map(({ key, name, ...restField }, index) => (
                              <Row
                                gutter={16}
                                key={key}
                                align="middle"
                                style={{ marginBottom: 12 }}
                              >
                                <Col span={12}>
                                  <Form.Item {...restField} name={[name, "question_2_codebookId"]}>
                                    <Select
                                      placeholder={t("register:select.trailer", "Trailer Type")}
                                      size="large"
                                      className="sDash_fullwidth-select"
                                    >
                                      {codebooks["TrailerType"] &&
                                        codebooks["TrailerType"]?.map((item) => (
                                          <Option key={item.id} value={item.id}>
                                            {item.name}
                                          </Option>
                                        ))}
                                    </Select>
                                  </Form.Item>
                                </Col>

                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, "question_2_trailerQTY"]} >
                                    <Input type="number" min={0} placeholder={t("register:trailer-qty", "Trailer QTY")} />
                                  </Form.Item>
                                </Col>

                                <Col span={4} style={{ display: "flex", gap: 8 }}>
                                  {/* Show remove icon only if index > 0 */}
                                  {index > 0 && (
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => remove(name)}
                                    />
                                  )}
                                  {/* Always show add button */}
                                  {index === fields.length - 1 && (
                                    <Button
                                      type="dashed"
                                      icon={<PlusOutlined />}
                                      onClick={() => add()}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>

                  {/* 3. Special License */}
                  <Row style={{ marginBottom: 12 }}>
                    <Col span={24}>
                      <Form.Item
                        name="question_7_codebookId"
                        label={t("register:questionnaire.special-license", "3. Special License")}
                      >
                        <Select
                          mode="multiple"
                          placeholder={t(
                            "register:select.special-license",
                            "Select Special License"
                          )}
                          size="large"
                          className="sDash_fullwidth-select"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {codebooks["SpecialLicence"] &&
                            codebooks["SpecialLicence"]?.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                    {/* 4. Type of transport */}
                    <Row style={{ marginBottom: 12 }}>
                    <Col span={24}>
                      <Form.Item
                        name="question_10_codebookId"
                        label={t("register:questionnaire.type-of-transport", "4. Select your type of transport")}
                      >
                        <Select
                          mode="multiple"
                          placeholder={t(
                            "register:select.type-of-transport",
                            "Select type of transport"
                          )}
                          size="large"
                          className="sDash_fullwidth-select"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {codebooks["TransportType"] &&
                            codebooks["TransportType"]?.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* 5. Vehicle Types */}
                  <Row>
                    <Col span={24}>
                      <Form.List name="vehicles" initialValue={[{}]}>
                        {(fields, { add, remove }) => (
                          <>
                            <label>
                              {t(
                                "register:questionnaire.vehicle-types",
                                "5. How many vehicles do you operate by vehicle type?"
                              )}
                            </label>
                            {fields.map(({ key, name, ...restField }, index) => (
                              <Row
                                gutter={16}
                                key={key}
                                align="middle"
                                style={{ marginBottom: 12 }}
                              >
                                <Col span={12}>
                                  <Form.Item {...restField} name={[name, "question_8_codebookId"]}>
                                    <Select
                                      placeholder={t("register:select.vehicle-type", "Vehicle Type")}
                                      size="large"
                                      className="sDash_fullwidth-select"
                                    >
                                      {codebooks["VehicleType"] &&
                                        codebooks["VehicleType"]?.map((item) => (
                                          <Option key={item.id} value={item.id}>
                                            {item.name}
                                          </Option>
                                        ))}
                                    </Select>
                                  </Form.Item>
                                </Col>

                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, "question_8_vehicleQTY"]}>
                                    <Input type="number" min={0} placeholder={t("register:vehicle-qty", "Vehicle QTY")} />
                                  </Form.Item>
                                </Col>

                                <Col span={4} style={{ display: "flex", gap: 8 }}>
                                  {index > 0 && (
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => remove(name)}
                                    />
                                  )}
                                  {index === fields.length - 1 && (
                                    <Button
                                      type="dashed"
                                      icon={<PlusOutlined />}
                                      onClick={() => add()}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>

                  {/* 6. Body Types */}
                  <Row>
                    <Col span={24}>
                      <Form.List name="body" initialValue={[{}]}>
                        {(fields, { add, remove }) => (
                          <>
                            <label>
                              {t(
                                "register:questionnaire.body-types",
                                "6. How many vehicles do you operate by body type?"
                              )}
                            </label>
                            {fields.map(({ key, name, ...restField }, index) => (
                              <Row
                                gutter={16}
                                key={key}
                                align="middle"
                                style={{ marginBottom: 12 }}
                              >
                                <Col span={12}>
                                  <Form.Item {...restField} name={[name, "question_9_codebookId"]}>
                                    <Select
                                      placeholder={t("register:select.body-type", "Body Type")}
                                      size="large"
                                      className="sDash_fullwidth-select"
                                    >
                                      {codebooks["BodyType"] &&
                                        codebooks["BodyType"]?.map((item) => (
                                          <Option key={item.id} value={item.id}>
                                            {item.name}
                                          </Option>
                                        ))}
                                    </Select>
                                  </Form.Item>
                                </Col>

                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, "question_9_bodyQTY"]}>
                                    <Input type="number" min={0} placeholder={t("register:body-qty", "Body QTY")} />
                                  </Form.Item>
                                </Col>

                                <Col span={4} style={{ display: "flex", gap: 8 }}>
                                  {index > 0 && (
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => remove(name)}
                                    />
                                  )}
                                  {index === fields.length - 1 && (
                                    <Button
                                      type="dashed"
                                      icon={<PlusOutlined />}
                                      onClick={() => add()}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>

                  {/* 7. CEMT permits */}
                  <Row>
                    <Col span={24}>
                      <Form.List
                        name="cemtPermits"
                        initialValue={[{}]}
                      >
                        {(fields, { add, remove }) => (
                          <>
                            <label>
                              {t(
                                "register:questionnaire.cemt-permits",
                                "7. How many CEMT permits do you possess, and for which countries?"
                              )}
                            </label>

                            {fields.map(({ key, name, ...restField }, index) => (
                              <Row
                                gutter={16}
                                key={key}
                                align="middle"
                                style={{ marginBottom: 12 }}
                              >
                                <Col span={10}>
                                  <Form.Item {...restField} name={[name, "question_3_codebookId"]}>
                                    <Select
                                      placeholder={t(
                                        "register:select.cemt-permit",
                                        "CEMT Permit"
                                      )}
                                      size="large"
                                      className="sDash_fullwidth-select"
                                    >
                                      {codebooks["CemtPermit"] &&
                                        codebooks["CemtPermit"]?.map((item) => (
                                          <Option key={item.id} value={item.id}>
                                            {item.name}
                                          </Option>
                                        ))}
                                    </Select>
                                  </Form.Item>
                                </Col>

                                <Col span={10}>
                                  <Form.Item {...restField} name={[name, "question_3_countryId"]}>
                                    <Select
                                      placeholder={t("register:country", "Country")}
                                      size="large"
                                      className="sDash_fullwidth-select"
                                    >
                                      {codebooks["Country"] &&
                                        codebooks["Country"]?.map((item) => (
                                          <Option key={item.id} value={item.id}>
                                            {item.name}
                                          </Option>
                                        ))}
                                    </Select>
                                  </Form.Item>
                                </Col>

                                <Col span={4} style={{ display: "flex", gap: "8px" }}>
                                  {index > 0 && (
                                    <Button
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => remove(name)}
                                    />
                                  )}
                                  {index === fields.length - 1 && (
                                    <Button
                                      type="dashed"
                                      icon={<PlusOutlined />}
                                      onClick={() => add()}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </Col>
                  </Row>

                  {/* 8. Certifications and licenses */}
                  <Row style={{ marginBottom: 12 }}>
                    <Col span={24}>
                      <label>
                        {t("register:questionnaire.certifications-and-licenses", "8. Which certifications and licenses does your company hold?")}
                      </label>
                      <Form.Item
                        name="question_4_codebookId"
                      >
                        <Select mode="multiple" placeholder={t("register:select.certification", "Select Certifications")}
                          size="large"
                          className="sDash_fullwidth-select"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {codebooks["Certificate"] &&
                            codebooks["Certificate"]?.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="question_5_codebookId"
                      >
                        <Select mode="multiple" placeholder={t("register:select.license", "Select License")}
                          size="large"
                          className="sDash_fullwidth-select mt-2"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {codebooks["License"] &&
                            codebooks["License"]?.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* 9. Countries of operation */}
                  <Row >
                    <Col span={24}>
                      <Form.Item
                        name="question_6_countryId"
                        label={t("register:questionnaire.operating-countries", "9. Which countries do you operate in?")}
                      >
                        <Select
                          mode="multiple"
                          size="large"
                          className="sDash_fullwidth-select"
                          placeholder={t("register:country", "Country")}
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {codebooks["OperatingCountry"] &&
                            codebooks["OperatingCountry"]?.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            {/* Email Verification Message */}
            <div className="request-div">
              <Row className="mb-4">
                <Col span={24}>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <strong>
                          <p className="text-blue-800 leading-5">
                            {t("register:email-verification-message", "Verification is required to confirm and validate the email address before proceeding further with the registration and activation of the account.")}
                          </p>
                        </strong>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="submit-button">
              <Row className="submit-container">
                <Col span={12}>
                  <Button
                    className="btn-next bg-white mb-3 md:mb-0 text-black"
                    type="default"
                    disabled={isSubmitting}
                    size="large"
                    htmlType="reset"
                  >
                    {t("requests:create.cancel-button", "Cancel")}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    className="btn-next bg-blue-500 ml-0 md:ml-4 submit-button"
                    type="primary"
                    // disabled={!isFormValid()}
                    htmlType="submit"
                    size="large"
                    loading={isSubmitting}
                  >
                    {t("requests:create.submit-button", "Submit")}
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        </Card>
      </div>
    </>
  );
};

export default CreateRequestsForm;
